import type { YearMonth } from '@/data/types';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * "2025-08" -> "Aug 2025". `null` means the role is current.
 *
 * Deliberately hand-rolled rather than using Intl.DateTimeFormat: resume dates
 * must render identically for every visitor regardless of locale, because the
 * same strings are asserted by the ATS parse harness (PLAN.md §8.5).
 */
export function formatYearMonth(value: YearMonth | null): string {
  if (value === null) return 'Present';

  const [yearPart, monthPart] = value.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`Invalid YearMonth: "${value}"`);
  }

  return `${MONTHS[month - 1]} ${year}`;
}

/** "Aug 2025 – Present" */
export function formatRange(start: YearMonth, end: YearMonth | null): string {
  return `${formatYearMonth(start)} \u2013 ${formatYearMonth(end)}`;
}

/** Whole months between two points, treating `null` as now. */
export function monthsBetween(start: YearMonth, end: YearMonth | null, now = new Date()): number {
  const [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end ? end.split('-').map(Number) : [now.getFullYear(), now.getMonth() + 1];
  return (ey - sy) * 12 + (em - sm);
}

/** "4 yrs 5 mos", or "5 mos" when under a year. */
export function formatDuration(start: YearMonth, end: YearMonth | null, now = new Date()): string {
  const total = Math.max(0, monthsBetween(start, end, now));
  const years = Math.floor(total / 12);
  const months = total % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
  if (months > 0) parts.push(`${months} mo${months === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(' ') : '0 mos';
}

/**
 * "Aug 2023 – May 2025" when months are known, "2015 – 2019" when they are not.
 * Deliberately does not guess a month: a fabricated month on an education entry
 * is exactly the kind of small inaccuracy that becomes a credibility question.
 */
export function formatEducationRange(entry: {
  startYear: number;
  endYear: number;
  startMonth?: number;
  endMonth?: number;
}): string {
  const { startYear, endYear, startMonth, endMonth } = entry;
  if (startMonth && endMonth) {
    return `${MONTHS[startMonth - 1]} ${startYear} – ${MONTHS[endMonth - 1]} ${endYear}`;
  }
  return `${startYear} – ${endYear}`;
}
